// @ts-nocheck
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, inject, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AssetMapNodeType } from '@api/model/assetMapNodeType';
import { MapHierarchy } from '@api/model/models';
import { Navigate } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import cytoscape from 'cytoscape';
import popper from 'cytoscape-popper';
import tippy from 'tippy.js';
import { TranslationService } from '../../transloco/services/translation.service';
import { cytomapStyles } from './cytomap-styles';

cytoscape.use(popper(() => { }));

@Component({
    selector: 'app-cytomap',
    templateUrl: './cytomap.component.html',
    styleUrls: ['./cytomap.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CytomapComponent implements OnChanges {
    @Input() public data: MapHierarchy;
    @Input() public elements: any;
    @Input() public assetMap: { [edgeDeviceId: string]: boolean };
    @Input() public assetMapState: any;
    @Input() public isTenantMap: boolean;
    @Input() public loaderId = 'map-loader';

    @Output() public nodeCollapsed = new EventEmitter<string>();
    @Output() public nodeExpanded = new EventEmitter<string>();

    @ViewChild('container', { static: true }) private container: ElementRef;

    private nodeTappedBefore: any;
    private tappedTimeout: any;

    private cy: cytoscape.Core;
    private styles: any = cytomapStyles;

    private layout = {
        name: 'breadthfirst',
        roots: '#Cloud',
    };

    private store = inject(Store);
    private translationService = inject(TranslationService);

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.data && changes.data.currentValue) {
            this.render();
        }
    }

    @HostListener('window:resize')
    public onResize() {
        if (this.cy) {
            this.cy.fit();
            this.cy.resize();
        }
    }

    private render() {
        if (this.container && this.data.edges && this.data.nodes) {
            let isInitialized = false;

            this.cy = cytoscape({
                container: this.container.nativeElement,
                elements: this.data,
                style: this.styles,
                layout: this.layout,
                wheelSensitivity: 0.2,
                boxSelectionEnabled: false,
                autoungrabify: true,
                fit: true,
            });

            this.cy.ready(() => this.initialize());
            this.cy.on('tap', (event: Event) => this.onNodeTap(event));
            this.cy.on('doubleTap', 'node', (event: Event) => this.navigateToNode(event.target));
            this.cy.on('mouseover', 'node', (node) => this.onNodeMouseOver(node));
            this.cy.on('mouseout', 'node', (node) => this.onNodeMouseOut(node));
            this.cy.on('render', () => {
                if (!isInitialized) {
                    this.cy.fit();
                    isInitialized = true;
                }
            });
        }
    }

    private onNodeTap(event: Event) {
        const node = event.target;
        if (this.tappedTimeout && node) {
            clearTimeout(this.tappedTimeout);
        }

        const shouldTriggerDoubleTap = this.nodeTappedBefore === node;
        if (shouldTriggerDoubleTap) {
            this.nodeTappedBefore = null;
            event.target.trigger('doubleTap', event);
        } else {
            // wait 300ms for next click to see if it is a double click
            // otherwise it is a single click and we toggle the node
            this.tappedTimeout = setTimeout(() => {
                this.nodeTappedBefore = null;
                this.toggleNode(node);
            }, 300);

            this.nodeTappedBefore = node;
        }
    }

    // @ts-ignore
    private collapseNode(node) {
        node.addClass('collapsed');
        for (const childNode of node.outgoers()) {
            this.collapseNode(childNode);
            childNode.hide();
            this.collapseNodeState(childNode);
        }
    }

    private collapseNodeInit(node) {
        node.addClass('collapsed');
        node.hide();
        for (const childNode of node.outgoers()) {
            this.collapseNodeInit(childNode);
        }
    }

    private expandNodeInit(node) {
        node.show();
        for (const childNode of node.outgoers()) {
            this.expandNodeInit(childNode);
        }
    }

    private expandNode(node) {
        if (node.hasClass('collapsed')) {
            node.removeClass('collapsed');
        }
        for (const childNode of node.outgoers()) {
            this.expandNode(childNode);
            childNode.show();
            this.expandeNodeState(childNode);
        }
    }

    private toggleNode(node) {
        if (node == null) {
            return;
        }

        if (node.data('id') === 'Cloud') {
            return;
        }
        const hasClassCollapsed = node.hasClass('collapsed');

        if (hasClassCollapsed) {
            this.expandNode(node);
        } else {
            this.collapseNode(node);
        }
    }

    private navigateToNode(node: CytoscriptNode) {
        const assetId = node.data('assetId');
        const deviceId = node.data('deviceId');
        if (!assetId && !deviceId) {
            return;
        }

        node.tippy.hide();

        if (assetId) {
            this.store.dispatch(new Navigate(['/devices', deviceId, 'assets', assetId]));
        } else {
            this.store.dispatch(new Navigate(['/devices', deviceId]));
        }
    }

    private onNodeMouseOver(node) {
        node.target.tippy.show();
    }

    private onNodeMouseOut(node) {
        node.target.tippy.hide();
    }

    private collapseNodeState(node) {
        this.nodeCollapsed.emit(node.data().id);
    }

    private expandeNodeState(node) {
        this.nodeExpanded.emit(node.data().id);
    }

    private initialize() {
        this.initializeNodes(this.assetMapState);
    }

    private initializeNodes(assetMapState: any) {
        let dummyDomEle = document.createElement('div');

        this.cy.elements().forEach((node) => {
            const ref = node.popperRef();
            const description = node.data('description');
            const statusDisplayName = node.data('color') === 'red' ? this.translationService.translate('defaults.offline') : this.translationService.translate('defaults.online');
            const statusStyle = node.data('color') === 'red' ? 'text-danger' : 'text-success';
            const status = `<span class="${statusStyle} fw-normal">${statusDisplayName}</span>`;
            const tooltip = !description ? status : `${description}<br/>${status}`;

            node.tippy = tippy(dummyDomEle, {
                getReferenceClientRect: ref.getBoundingClientRect,
                trigger: 'manual',
                content: () => {
                    const content = document.createElement('div');
                    content.innerHTML = tooltip;
                    return content;
                },
            });

            this.initializeExpandCollapse(node, assetMapState);
        });
    }

    private initializeExpandCollapse(node: any, assetMapState: any) {
        if (this.isTenantMap) {
            const isEdgeDevice = node.data('nodeType') === AssetMapNodeType.NUMBER_1;
            if (isEdgeDevice) {
                node.addClass('collapsed');
                for (const childNode of node.outgoers()) {
                    this.collapseNodeInit(childNode);
                    childNode.hide();
                }
            }
        } else {
            for (const childNode of node.outgoers()) {
                this.expandNodeInit(childNode);
            }
        }

        if (assetMapState) {
            this.collapseExpandNodeBasedOnState(node, assetMapState);
        }
    }

    private collapseExpandNodeBasedOnState(node: any, assetMapState: any) {
        const checkedNodeId = node.data('id');
        const isShowing = assetMapState[checkedNodeId];
        if (isShowing === true) {
            node.show();
            for (const parent of node.incomers()) {
                parent.removeClass('collapsed');
            }
        }

        if (isShowing === false) {
            for (const parent of node.incomers()) {
                parent.addClass('collapsed');
            }
            node.addClass('collapsed');
            const isEdgeDevice = node.data('nodeType') === AssetMapNodeType.NUMBER_1;
            if (!isEdgeDevice) {
                node.hide();
                for (const childNode of node.outgoers()) {
                    childNode.hide();
                }
            }
        }
    }
}
